import { differenceInCalendarDays, format, isSameDay, parseISO, startOfDay, endOfDay } from 'date-fns';
import { Plus } from 'lucide-react';
import { CalendarBooking } from '../../lib/bookings';
import { truncateGcalLocation } from '../../lib/gcalEventFormat';

export type AssignedResourceDisplay = {
  id: string;
  name: string;
  avatarUrl: string | null;
  initials: string;
};

export function filterBookingsForDay(bookings: CalendarBooking[], day: Date): CalendarBooking[] {
  const start = startOfDay(day).getTime();
  const end = endOfDay(day).getTime();

  return bookings
    .filter((b) => {
      if (b.extendedProps?.is_buffer_block) return false;
      try {
        const bStart = parseISO(b.start).getTime();
        const bEnd = parseISO(b.end).getTime();
        return bStart < end && bEnd > start;
      } catch {
        return false;
      }
    })
    .sort((a, b) => {
      try {
        return parseISO(a.start).getTime() - parseISO(b.start).getTime();
      } catch {
        return 0;
      }
    });
}

function parseBookingDateTime(value: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseISO(`${value}T00:00:00`);
  }
  return parseISO(value);
}

function formatEventSchedule(booking: CalendarBooking): { start: string; end: string } | null {
  try {
    const start = parseBookingDateTime(booking.start);
    const end = parseBookingDateTime(booking.end);
    const isTimed =
      !booking.allDay &&
      booking.start.includes('T') &&
      booking.end.includes('T');

    if (isTimed) {
      return {
        start: format(start, 'MMM d · h:mm a'),
        end: format(end, 'MMM d · h:mm a'),
      };
    }

    return {
      start: format(start, 'MMM d'),
      end: format(end, 'MMM d'),
    };
  } catch {
    return null;
  }
}

function formatPersonName(first?: string | null, last?: string | null): string {
  return `${first || ''} ${last || ''}`.trim();
}

function toInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?'
  );
}

/** Assigned team members (with avatars) for the day detail list. */
export function getAssignedResources(
  booking: CalendarBooking,
  resourceById?: Record<string, AssignedResourceDisplay>
): AssignedResourceDisplay[] {
  const props = booking.extendedProps;

  if (props?.resources?.length) {
    return props.resources.map((r) => {
      const name = formatPersonName(r.first_name, r.last_name) || 'Unknown';
      return {
        id: r.id,
        name,
        avatarUrl: r.avatar_url ?? resourceById?.[r.id]?.avatarUrl ?? null,
        initials: toInitials(name),
      };
    });
  }

  const id = booking.resourceId || props?.resource_id;
  if (id && resourceById?.[id]) {
    return [resourceById[id]];
  }

  if (props?.is_time_off) {
    const name = typeof props.customer === 'string' ? props.customer.trim() : '';
    if (name) {
      return [
        {
          id: id || name,
          name,
          avatarUrl: id ? resourceById?.[id]?.avatarUrl ?? null : null,
          initials: toInitials(name),
        },
      ];
    }
  }

  return [];
}

function AssignedResourceAvatars({ resources }: { resources: AssignedResourceDisplay[] }) {
  if (resources.length === 0) return null;

  const visible = resources.slice(0, 5);
  const overflow = resources.length - visible.length;

  const title = resources.map((r) => r.name).join(', ');

  return (
    <div
      className="flex items-center shrink-0 mt-1"
      role="img"
      aria-label={title}
      title={title}
    >
      {visible.map((r, i) => (
        <div
          key={r.id}
          className="relative rounded-full ring-2 ring-white"
          style={{ marginLeft: i === 0 ? 0 : -6, zIndex: visible.length - i }}
        >
          {r.avatarUrl ? (
            <img
              src={r.avatarUrl}
              alt={r.name}
              className="h-6 w-6 rounded-full object-cover bg-gray-100"
            />
          ) : (
            <div className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <span className="text-[9px] font-semibold text-white leading-none">{r.initials}</span>
            </div>
          )}
        </div>
      ))}
      {overflow > 0 ? (
        <div
          className="h-6 w-6 rounded-full bg-gray-200 ring-2 ring-white flex items-center justify-center"
          style={{ marginLeft: -6, zIndex: 0 }}
        >
          <span className="text-[8px] font-semibold text-gray-600">+{overflow}</span>
        </div>
      ) : null}
    </div>
  );
}

function getEventHeading(booking: CalendarBooking): { title: string; subtitle: string | null } {
  const eventTitle = (booking.title || '').trim();
  const customer =
    typeof booking.extendedProps?.customer === 'string'
      ? booking.extendedProps.customer.trim()
      : '';

  if (booking.extendedProps?.is_time_off) {
    return { title: eventTitle || 'Time Off', subtitle: null };
  }

  if ((booking.extendedProps as { is_gcal?: boolean })?.is_gcal) {
    return { title: eventTitle || 'Google Event', subtitle: null };
  }

  if (customer && eventTitle && customer !== eventTitle) {
    return { title: eventTitle, subtitle: customer };
  }

  if (customer) {
    return { title: customer, subtitle: null };
  }

  return { title: eventTitle || 'Booking', subtitle: null };
}

function getBookingSpanBadge(booking: CalendarBooking): 'same-day' | 'multi-day' | null {
  if (booking.extendedProps?.is_buffer_block) return null;

  try {
    const start = parseBookingDateTime(booking.start);
    const end = parseBookingDateTime(booking.end);
    const startDay = startOfDay(start);
    let endDay = startOfDay(end);

    const isDateOnly =
      booking.allDay || (!booking.start.includes('T') && !booking.end.includes('T'));

    if (
      isDateOnly &&
      end.getHours() === 0 &&
      end.getMinutes() === 0 &&
      differenceInCalendarDays(end, start) === 1
    ) {
      endDay = startDay;
    }

    if (differenceInCalendarDays(endDay, startDay) <= 0) {
      return 'same-day';
    }
    return 'multi-day';
  } catch {
    return null;
  }
}

function canOpenBookingModal(booking: CalendarBooking): boolean {
  if (booking.extendedProps?.is_time_off) return false;
  if (booking.extendedProps?.is_buffer_block) return false;
  if (booking.extendedProps?.is_task) return false;
  if ((booking.extendedProps as { is_gcal?: boolean })?.is_gcal) return false;
  if (booking.id.startsWith('fieldquote_')) return false;
  if (booking.id.startsWith('timeoff_')) return false;
  if (booking.id.startsWith('gcal_')) return false;
  return true;
}

interface MiniMonthDayDetailProps {
  day: Date;
  bookings: CalendarBooking[];
  resourceById?: Record<string, AssignedResourceDisplay>;
  iosStyled?: boolean;
  onBookingClick?: (bookingId: string) => void;
  /** Opens CalendarV2-style booking type picker for this day (9:00 AM). */
  onAddClick?: () => void;
  showAddButton?: boolean;
}

export default function MiniMonthDayDetail({
  day,
  bookings,
  resourceById,
  iosStyled = false,
  onBookingClick,
  onAddClick,
  showAddButton = false,
}: MiniMonthDayDetailProps) {
  const dayEvents = filterBookingsForDay(bookings, day);
  const isToday = isSameDay(day, new Date());

  return (
    <div
      className={
        iosStyled
          ? 'border-t border-black/[0.06] bg-[#f8f9fb] px-1.5 py-1.5 sm:px-2 sm:py-2'
          : 'border-t border-gray-100 bg-gray-50/80 px-2 py-2'
      }
    >
      <div className="flex items-center justify-between gap-2 mb-1 sm:mb-1.5 px-0.5">
        <p className="text-[10px] sm:text-[11px] font-semibold text-gray-700 tracking-tight min-w-0 truncate">
          <span className="sm:hidden">{format(day, 'EEE, MMM d')}</span>
          <span className="hidden sm:inline">{format(day, 'EEEE, MMM d')}</span>
          {isToday ? (
            <span className="ml-1 text-[9px] sm:text-[10px] font-medium text-[#FF3B30]">Today</span>
          ) : null}
        </p>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] text-gray-400 tabular-nums">
            {dayEvents.length} {dayEvents.length === 1 ? 'item' : 'items'}
          </span>
          {showAddButton && onAddClick ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAddClick();
              }}
              className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 active:scale-[0.98] touch-manipulation"
              aria-label={`Create booking on ${format(day, 'MMMM d')}`}
              title="Add booking"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
      </div>

      {dayEvents.length === 0 ? (
        <p className="text-[11px] text-gray-400 px-0.5 py-2">Nothing scheduled</p>
      ) : (
        <ul
          className={`overflow-y-auto ios-momentum-scroll space-y-1 pr-0.5 ${
            iosStyled ? 'max-h-[min(180px,28vh)] sm:max-h-[132px]' : 'max-h-[132px]'
          }`}
        >
          {dayEvents.map((booking) => {
            const isGcal = (booking.extendedProps as { is_gcal?: boolean })?.is_gcal;
            const isTimeOff = booking.extendedProps?.is_time_off;
            const isFieldQuote = booking.extendedProps?.is_field_quote;
            const isTask = booking.extendedProps?.is_task;
            const openable = canOpenBookingModal(booking) && !!onBookingClick;
            const gcalProps = booking.extendedProps as {
              is_gcal_digital?: boolean;
              is_gcal_in_person?: boolean;
              gcal_location?: string | null;
              meet_link?: string | null;
            };

            const accent = isGcal
              ? '#4285f4'
              : isTimeOff
                ? '#111827'
                : isFieldQuote
                  ? '#f97316'
                  : isTask
                    ? '#8b5cf6'
                    : booking.backgroundColor || '#3b82f6';
            const schedule = formatEventSchedule(booking);
            const assignedResources = getAssignedResources(booking, resourceById);
            const { title: headingTitle, subtitle: headingSubtitle } = getEventHeading(booking);
            const spanBadge = getBookingSpanBadge(booking);

            return (
              <li key={`${booking.id}-${booking.start}`}>
                <button
                  type="button"
                  disabled={!openable}
                  onClick={() => openable && onBookingClick?.(booking.id)}
                  className={`relative w-full text-left rounded-md sm:rounded-lg border border-black/[0.05] bg-white pl-1.5 sm:pl-2 py-1 sm:py-1.5 flex gap-1.5 sm:gap-2 items-start transition-colors touch-manipulation ${
                    spanBadge ? 'pr-[4rem] sm:pr-[4.5rem]' : 'pr-1.5 sm:pr-2'
                  } ${openable ? 'active:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {spanBadge ? (
                    <span
                      className={`absolute top-1.5 right-1.5 rounded-md px-1.5 py-0.5 text-[9px] font-semibold leading-none tracking-wide ${
                        spanBadge === 'same-day'
                          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80'
                          : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200/80'
                      }`}
                    >
                      {spanBadge === 'same-day' ? 'Same day' : 'Multi day'}
                    </span>
                  ) : null}
                  <span
                    className="w-0.5 self-stretch rounded-full shrink-0 min-h-[28px]"
                    style={{ backgroundColor: accent }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-gray-900 truncate leading-tight">
                      {headingTitle}
                    </p>
                    {headingSubtitle ? (
                      <p className="text-[10px] text-gray-600 truncate mt-0.5 leading-tight">
                        {headingSubtitle}
                      </p>
                    ) : null}
                    {schedule ? (
                      <div className="text-[10px] text-gray-500 mt-0.5 space-y-0.5 leading-snug">
                        <p>
                          <span className="text-gray-400">Start </span>
                          {schedule.start}
                        </p>
                        <p>
                          <span className="text-gray-400">End </span>
                          {schedule.end}
                        </p>
                      </div>
                    ) : null}
                    {isGcal && (gcalProps.is_gcal_digital || gcalProps.is_gcal_in_person) ? (
                      <p className="text-[10px] text-blue-600 mt-0.5 truncate">
                        {gcalProps.is_gcal_digital ? 'Digital' : ''}
                        {gcalProps.is_gcal_digital && gcalProps.is_gcal_in_person ? ' · ' : ''}
                        {gcalProps.is_gcal_in_person
                          ? gcalProps.gcal_location
                            ? truncateGcalLocation(gcalProps.gcal_location, 28)
                            : 'In person'
                          : ''}
                      </p>
                    ) : null}
                    <AssignedResourceAvatars resources={assignedResources} />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
